import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icons } from '../shared/Icons';
import './AIChatbot.css';

const ANTHROPIC_API_KEY = import.meta.env.VITE_ANTHROPIC_API_KEY;

const SUPPORT_SYSTEM_PROMPT = `You are AI4Solar Support, a helpful and knowledgeable support assistant for AI4Solar - a comprehensive solar energy monitoring and analytics platform.

Your role is to assist users with:
- Account and billing questions
- Technical issues with monitoring dashboards
- API integration and developer support
- Understanding solar energy metrics and data
- Troubleshooting inverter connections and data collection
- General solar energy monitoring questions
- Navigation and feature explanations

Guidelines:
- Be concise, friendly, and professional
- Use simple language, avoid unnecessary jargon
- If you don't know something specific, offer to connect the user with human support
- For complex technical issues, recommend contacting support@ai4solar.org
- For billing issues, direct to billing@ai4solar.org or the billing portal
- Always be helpful and solution-oriented

AI4Solar Features you can help with:
- Real-time energy monitoring dashboard
- Historical data and analytics
- Weather-based production forecasting
- Alert management and notifications
- API access for developers
- Mobile app support (iOS & Android)
- Multi-site fleet management
- Battery storage monitoring`;

const QUICK_ACTIONS = [
  { label: 'Dashboard issues', prompt: 'I\'m having trouble with my monitoring dashboard' },
  { label: 'API help', prompt: 'I need help with the AI4Solar API' },
  { label: 'Billing question', prompt: 'I have a question about my billing' },
  { label: 'Connect inverter', prompt: 'How do I connect my inverter to AI4Solar?' },
];

export default function AIChatbot({ isOpen: externalIsOpen, onToggle }) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onToggle || setInternalIsOpen;

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I\'m AI4Solar Support, your support assistant. How can I help you today?',
      timestamp: new Date(),
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Build conversation history for Claude (excluding timestamps)
      const conversationHistory = [...messages, userMessage]
        .filter(m => m.role !== 'system')
        .map(m => ({
          role: m.role,
          content: m.content,
        }));

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20250514',
          max_tokens: 1024,
          system: SUPPORT_SYSTEM_PROMPT,
          messages: conversationHistory,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `API error: ${response.status}`);
      }

      const data = await response.json();
      const assistantMessage = {
        role: 'assistant',
        content: data.content[0].text,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Chat error:', err);
      setError(err.message || 'Failed to send message. Please try again.');

      // Add error message to chat
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment, or contact our support team at support@ai4solar.org for immediate assistance.',
        timestamp: new Date(),
        isError: true,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickAction = (prompt) => {
    sendMessage(prompt);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const showQuickActions = messages.length <= 1 && !isLoading;

  return (
    <div className="ai-chatbot">
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            className="chatbot-toggle"
            onClick={() => setIsOpen(true)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Open AI Support Chat"
          >
            <div className="chatbot-toggle-icon">
              <Icons.brain size={28} />
            </div>
            <span className="chatbot-toggle-pulse" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chatbot-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header */}
            <div className="chatbot-header">
              <div className="chatbot-header-info">
                <div className="chatbot-avatar">
                  <Icons.brain size={20} />
                </div>
                <div className="chatbot-header-text">
                  <h4>AI4Solar Support</h4>
                  <span className="chatbot-status">
                    <span className="status-dot" />
                    {isLoading ? 'Typing...' : 'Online'}
                  </span>
                </div>
              </div>
              <button
                className="chatbot-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                <Icons.x size={20} />
              </button>
            </div>

            {/* Messages */}
            <div className="chatbot-messages">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  className={`chatbot-message ${message.role} ${message.isError ? 'error' : ''}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {message.role === 'assistant' && (
                    <div className="message-avatar">
                      <Icons.brain size={16} />
                    </div>
                  )}
                  <div className="message-content">
                    <p>{message.content}</p>
                    <span className="message-time">{formatTime(message.timestamp)}</span>
                  </div>
                </motion.div>
              ))}

              {/* Typing Indicator */}
              {isLoading && (
                <motion.div
                  className="chatbot-message assistant"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="message-avatar">
                    <Icons.brain size={16} />
                  </div>
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Quick Actions */}
              {showQuickActions && (
                <div className="quick-actions">
                  <p className="quick-actions-label">Quick questions:</p>
                  <div className="quick-actions-grid">
                    {QUICK_ACTIONS.map((action, index) => (
                      <button
                        key={index}
                        className="quick-action-btn"
                        onClick={() => handleQuickAction(action.prompt)}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form className="chatbot-input" onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                aria-label="Send message"
              >
                <Icons.arrowRight size={20} />
              </button>
            </form>

            {/* Footer */}
            <div className="chatbot-footer">
              <span>Powered by</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              <span>Claude</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
